// eslint-disable @typescript-eslint/no-explicit-any

import { useCallback } from 'react';
import Datasheet from '@scalartech/react-datasheet';
import { isEmpty } from 'lodash';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import ScalarDatasheetProps, { AttributesRendererResponseType, Selection } from './ScalarDatasheet.props';
import useDatasheetClipboardPaste from './useDatasheetClipboardPaste';
import { EventType, useEventBus } from '../../context';
import { ValueViewer } from '../Spreadsheet/components';

const isLegendCell = (cell?: ICell) =>
  Boolean(cell?.isLegend || (typeof cell?.className === 'string' && cell.className.includes('legend')));

const ScalarDatasheet = ({
  valueViewer = ValueViewer,
  valueRenderer,
  selected: controlledSelected,
  ...props
}: ScalarDatasheetProps) => {
  const { clearSelectedFlag, setClearSelected, setSelectedCell, selectedCell, isColumnDragging } =
    useFeatureSpreadsheet();
  const { data, virtualization } = props;
  const renderValue = valueRenderer || ((cell: ICell) => cell.value);
  const { publish } = useEventBus();

  const { selected, setSelected, setMaxAllowedCells, containerRef, handleOnPaste } = useDatasheetClipboardPaste({
    data,
    onCellsChanged: props.onCellsChanged,
    clearSelectedFlag,
    setClearSelected,
    enableCopyFeedback: props.enableCopyFeedback,
  });
  const datasheetSelected = controlledSelected === undefined ? selected : controlledSelected;

  const attributesRenderer = useCallback(
    (cell: ICell): AttributesRendererResponseType => ({
      ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
      'data-parent': cell.parent ?? '',
      ...(cell.x !== undefined ? { 'data-row-index': String(cell.x) } : {}),
    }),
    []
  );

  const handleOnSelect = useCallback(
    (selection: Selection | null) => {
      if (isColumnDragging) {
        return;
      }
      if (!selection) {
        setSelected(selection);
        return;
      }
      if (props.disableLegendSelection && selection) {
        const { start, end } = selection;
        const startCell = data?.[start.i]?.[start.j];
        const endCell = data?.[end.i]?.[end.j];

        if (isLegendCell(startCell) || isLegendCell(endCell)) {
          setSelected(null);
          return;
        }
      }
      const { start, end } = selection;
      const maxAllowedRows = end.i - start.i + 1;
      const maxAllowedColumns = end.j - start.j + 1;

      setMaxAllowedCells({
        maxAllowedRows,
        maxAllowedColumns,
      });
      const isTotalColumn = data[1]?.some(({ isTotal }) => isTotal);
      if (setSelectedCell && !isTotalColumn && !isEmpty(start)) {
        const cellInfo: ICell = data?.[start.i]?.[start.j];
        if (!cellInfo) {
          setSelected(selection);
          return;
        }

        const { insideLedger, allowInsideLedger } = cellInfo;
        if (!insideLedger || allowInsideLedger) {
          // Capture the end cell so the RowIndicator can compute
          // the indicator height for multi-row selections.
          const endCellInfo: ICell | undefined = data?.[end.i]?.[end.j];
          const selectedCellData = {
            ...selection,
            rowNumber: start.i,
            cellInfo,
            ...(endCellInfo ? { endCellInfo } : {}),
          };
          publish(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, [selectedCellData, selectedCell]);
          setSelectedCell(selectedCellData);
        }
      }
      setSelected(selection);
    },
    [
      data,
      props.disableLegendSelection,
      setSelectedCell,
      publish,
      selectedCell,
      isColumnDragging,
      setMaxAllowedCells,
      setSelected,
    ]
  );

  const handleMouseDownCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!props.disableLegendSelection) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const legendCell = target?.closest('td.legend, th.legend');
      const canSelectColumnLegend = props.allowColumnLegendSelection && legendCell?.matches('.col-legend');
      const canSelectRowLegend = props.allowRowLegendSelection && legendCell?.matches('.row-legend');
      if (legendCell && (canSelectColumnLegend || canSelectRowLegend)) {
        const rowIndex = legendCell.getAttribute('data-row-index');
        const columnLegend = legendCell.getAttribute('data-column-legend');
        const spreadsheet = containerRef.current?.closest<HTMLElement>('.spreadsheet-container');
        const contentColumns = spreadsheet?.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
        const ownerWindow = legendCell.ownerDocument.defaultView ?? window;
        let referenceCell: HTMLElement | null | undefined;
        if (canSelectRowLegend && rowIndex !== null) {
          const escapedRowIndex = ownerWindow.CSS.escape(rowIndex);
          referenceCell =
            contentColumns?.querySelector<HTMLElement>(
              `td[data-row-index="${escapedRowIndex}"][data-column-legend="\u03B1"]`
            ) ?? contentColumns?.querySelector<HTMLElement>(`td[data-row-index="${escapedRowIndex}"]:not(.legend)`);
        } else if (canSelectColumnLegend && columnLegend) {
          referenceCell = contentColumns?.querySelector<HTMLElement>(
            `td[data-column-legend="${ownerWindow.CSS.escape(columnLegend)}"]:not(.col-legend)`
          );
        }

        if (referenceCell) {
          event.preventDefault();
          event.stopPropagation();
          // Reuse a regular XLSX cell's selection path so row and column indicators stay consistent.
          referenceCell.dispatchEvent(
            new ownerWindow.MouseEvent('mousedown', {
              bubbles: true,
              cancelable: true,
              clientX: event.clientX,
              clientY: event.clientY,
              shiftKey: event.shiftKey,
            })
          );
        }
        return;
      }

      if (legendCell) {
        setSelected(null);
      }
    },
    [
      containerRef,
      props.allowColumnLegendSelection,
      props.allowRowLegendSelection,
      props.disableLegendSelection,
      setSelected,
    ]
  );

  return (
    <div ref={containerRef} onMouseDownCapture={handleMouseDownCapture}>
      <Datasheet
        parsePaste={handleOnPaste}
        onSelect={handleOnSelect}
        selected={datasheetSelected}
        // any is used because the library does not have typing correctly configured
        // when migrating from js->ts the props incoming to this component are typed
        // but the library has its own typing it expects
        valueRenderer={renderValue as any}
        virtualization={virtualization}
        valueViewer={valueViewer as any}
        attributesRenderer={props.attributesRenderer ?? (attributesRenderer as any)}
        cellRenderer={props.cellRenderer as any}
        dataEditor={props.dataEditor as any}
        onCellsChanged={props.onCellsChanged as any}
        rowRenderer={props.rowRenderer as any}
        data={props.data as any}
        className={props.className as any}
        dataRenderer={props.dataRenderer as any}
        isCellNavigable={props.isCellNavigable as any}
      />
    </div>
  );
};

export default ScalarDatasheet;
